import { Link } from 'react-router-dom';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import { Check, X, MessageCircle, UserPlus, Loader2, Dumbbell, Mail, Users } from 'lucide-react';
import { getImageUrl } from '../../../utils/imageUtils';

const API = process.env.REACT_APP_BACKEND_URL;

// ---------- Individual notification renderers ----------

export const UnreadDmItem = ({ count, onClose }) => (
  <Link
    to="/dm"
    className="flex items-center gap-3 p-4 hover:bg-white/5 transition-colors border-b border-border"
    onClick={onClose}
    data-testid="unread-dm-notification"
  >
    <div className="h-10 w-10 bg-blue-500/20 rounded-full flex items-center justify-center">
      <MessageCircle className="h-5 w-5 text-blue-400" />
    </div>
    <div className="flex-1">
      <p className="text-white font-medium">
        {count} unread message{count > 1 ? 's' : ''}
      </p>
      <p className="text-muted-foreground text-sm">Tap to view</p>
    </div>
  </Link>
);

export const WelcomeNotificationItem = ({ notif, onClick }) => (
  <Link
    to="/messages"
    className="flex items-center gap-3 p-4 hover:bg-white/5 transition-colors border-b border-border"
    onClick={onClick}
    data-testid={`welcome-notif-${notif.id}`}
  >
    <div className="h-10 w-10 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
      <Mail className="h-5 w-5 text-primary" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-white font-medium">Welcome to Ascend & Surpass!</p>
      <p className="text-muted-foreground text-sm">Tap to read your welcome message</p>
    </div>
  </Link>
);

export const WorkoutAssignedItem = ({ notif, onDismiss, onClose }) => (
  <div
    className="p-4 border-b border-border last:border-0 hover:bg-white/5 transition-colors"
    data-testid={`workout-notif-${notif.id}`}
  >
    <div className="flex items-start gap-3">
      <div className="h-10 w-10 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
        <Dumbbell className="h-5 w-5 text-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-white">New Workout Assigned</p>
        <p className="text-primary text-sm font-medium">{notif.workout_title}</p>
        <p className="text-muted-foreground text-xs mt-1">Personalized for you</p>
        <div className="flex gap-2 mt-2">
          <Link
            to={`/workouts/${notif.workout_id}`}
            className="flex-1"
            onClick={() => { onDismiss(notif.id); onClose(); }}
          >
            <Button size="sm" className="bg-primary text-black hover:bg-primary/90 w-full">
              Start Workout
            </Button>
          </Link>
          <Button size="sm" variant="outline" onClick={() => onDismiss(notif.id)}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  </div>
);

export const BuddyRequestItem = ({ notif, onDismiss, onClose }) => (
  <div
    className="p-4 border-b border-border last:border-0 hover:bg-white/5 transition-colors"
    data-testid={`buddy-notif-${notif.id}`}
  >
    <div className="flex items-start gap-3">
      <div className="h-10 w-10 bg-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0">
        <Users className="h-5 w-5 text-blue-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-white">Buddy Request</p>
        <p className="text-blue-400 text-sm font-medium">{notif.from_user_name}</p>
        <p className="text-muted-foreground text-xs">wants to be your workout buddy</p>
        <div className="flex gap-2 mt-2">
          <Link
            to="/buddies"
            className="flex-1"
            onClick={() => { onDismiss(notif.id); onClose(); }}
          >
            <Button size="sm" className="bg-blue-600 text-white hover:bg-blue-700 w-full">
              View Request
            </Button>
          </Link>
          <Button size="sm" variant="outline" onClick={() => onDismiss(notif.id)}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  </div>
);

// Renders the action button (or null) for a buddy-workout notification.
// Extracted so the parent JSX isn't littered with nested ternaries / IIFEs.
const BuddyWorkoutActionLink = ({ notif, onDismiss, onClose }) => {
  // Ended session → no destination, leave only the dismiss "X".
  if (notif.type === 'buddy_session_ended') return null;

  // Live freestyle invites have workout_id='live' (no real workout page).
  const isLiveOnly = !notif.workout_id || notif.workout_id === 'live';
  const target = isLiveOnly && notif.invite_id
    ? `/live-workout/${notif.invite_id}`
    : notif.workout_id
      ? `/workouts/${notif.workout_id}`
      : '/buddies';
  const label = notif.type === 'buddy_workout_invite'
    ? (isLiveOnly ? 'Join Live' : 'View Workout')
    : 'Go to Workout';

  return (
    <Link
      to={target}
      className="flex-1"
      onClick={() => { onDismiss(notif.id); onClose(); }}
    >
      <Button size="sm" className={`w-full ${
        notif.type === 'buddy_workout_invite'
          ? 'bg-orange-600 text-white hover:bg-orange-700'
          : 'bg-primary text-black hover:bg-primary/90'
      }`}>
        {label}
      </Button>
    </Link>
  );
};

export const BuddyWorkoutItem = ({ notif, processingId, onAccept, onDecline, onDismiss, onClose }) => {
  const completed = notif.type === 'buddy_workout_completed';
  const ended = notif.type === 'buddy_session_ended';

  const iconBg = completed ? 'bg-green-500/20' : ended ? 'bg-zinc-500/20' : 'bg-orange-500/20';
  const iconColor = completed ? 'text-green-400' : ended ? 'text-zinc-400' : 'text-orange-400';
  const nameColor = completed ? 'text-green-400' : ended ? 'text-zinc-300' : 'text-orange-400';

  const titleMap = {
    buddy_workout_invite: 'Workout Invite',
    buddy_workout_accepted: 'Invite Accepted!',
    buddy_session_ended: 'Live Session Ended',
    buddy_workout_completed: 'Buddy Completed Workout!',
  };

  return (
    <div
      className="p-4 border-b border-border last:border-0 hover:bg-white/5 transition-colors"
      data-testid={`buddy-workout-notif-${notif.id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${iconBg}`}>
          <Dumbbell className={`h-5 w-5 ${iconColor}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-white">{titleMap[notif.type] || titleMap.buddy_workout_completed}</p>
          <p className={`text-sm font-medium ${nameColor}`}>{notif.from_user_name}</p>
          <p className="text-muted-foreground text-xs">{notif.workout_title}</p>
          {notif.both_done && (
            <p className="text-primary text-xs font-medium mt-1">You both crushed it!</p>
          )}
          <div className="flex gap-2 mt-2 flex-wrap">
            {notif.type === 'buddy_workout_invite' && notif.invite_id && (
              <>
                <Button
                  size="sm"
                  onClick={() => onAccept(notif)}
                  disabled={processingId === notif.id}
                  className="bg-primary text-black hover:bg-primary/90"
                  data-testid={`workout-invite-accept-${notif.id}`}
                >
                  {processingId === notif.id
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <><Check className="h-4 w-4 mr-1" /> Accept</>}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onDecline(notif)}
                  disabled={processingId === notif.id}
                  data-testid={`workout-invite-decline-${notif.id}`}
                >
                  <X className="h-4 w-4 mr-1" /> Decline
                </Button>
              </>
            )}
            <BuddyWorkoutActionLink notif={notif} onDismiss={onDismiss} onClose={onClose} />
            <Button size="sm" variant="ghost" onClick={() => onDismiss(notif.id)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const NewUserItem = ({ notif, onDismiss, onClose }) => (
  <div
    className="p-4 border-b border-border last:border-0 hover:bg-white/5 transition-colors"
    data-testid={`new-user-notif-${notif.id}`}
  >
    <div className="flex items-start gap-3">
      <div className="h-10 w-10 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
        <UserPlus className="h-5 w-5 text-green-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-white">New User Registered</p>
        <p className="text-green-400 text-sm font-medium">{notif.user_name}</p>
        <p className="text-muted-foreground text-xs">{notif.user_email}</p>
        <div className="flex gap-2 mt-2">
          <Link
            to={notif.user_id ? `/admin/users?viewUser=${notif.user_id}` : '/admin/users'}
            className="flex-1"
            onClick={() => { onDismiss(notif.id); onClose(); }}
            data-testid={`new-user-view-admin-${notif.id}`}
          >
            <Button size="sm" className="bg-green-600 text-white hover:bg-green-700 w-full">
              View in Admin
            </Button>
          </Link>
          <Button size="sm" variant="outline" onClick={() => onDismiss(notif.id)}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  </div>
);

export const FollowRequestItem = ({ notif, processingId, onAccept, onReject, onClose }) => (
  <div
    className="p-4 border-b border-border last:border-0 hover:bg-white/5 transition-colors"
    data-testid={`follow-request-${notif.id}`}
  >
    <div className="flex items-start gap-3">
      <Link to={`/user/${notif.user?.id}`} onClick={onClose}>
        <Avatar className="h-10 w-10 border border-border">
          <AvatarImage src={getImageUrl(notif.user?.profile_picture)} />
          <AvatarFallback className="bg-primary/20 text-primary">
            {notif.user?.name?.charAt(0) || 'U'}
          </AvatarFallback>
        </Avatar>
      </Link>
      <div className="flex-1 min-w-0">
        <Link
          to={`/user/${notif.user?.id}`}
          className="font-medium text-white hover:text-primary transition-colors"
          onClick={onClose}
        >
          {notif.user?.name}
        </Link>
        <p className="text-muted-foreground text-sm truncate">wants to follow you</p>
        <div className="flex gap-2 mt-2">
          <Button
            size="sm"
            className="bg-primary text-black hover:bg-primary/90 flex-1"
            onClick={() => onAccept(notif.id, notif.user?.name)}
            disabled={processingId === notif.id}
            data-testid={`accept-request-${notif.id}`}
          >
            {processingId === notif.id
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <><Check className="h-4 w-4 mr-1" /> Accept</>}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="flex-1"
            onClick={() => onReject(notif.id, notif.user?.name)}
            disabled={processingId === notif.id}
            data-testid={`reject-request-${notif.id}`}
          >
            <X className="h-4 w-4 mr-1" /> Reject
          </Button>
        </div>
      </div>
    </div>
  </div>
);

// API is needed by callers that render asset URLs (kept exported for parity).
export { API };
